import { useEffect, useState } from "react";
import { connect } from "react-redux";

import { format } from "date-fns";
import { useSnackbar } from "notistack";

import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { createOmniPaginationObject, getConfigurationVersion } from "../../utils/helpers";
import { deepCopyObject, handleDownloadConversation } from "../../utils/helpers.js";
import AlertDialog from "../dialogs//alertDialog";
import BotpressAssessmentDialog from "../dialogs/botpressAssessmentDialog.js";
import BotpressCompareConversationsDialog from "../dialogs/botpressCompareConversationsDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressInteractiveAssessmentDialog from "../dialogs/botpressInteractiveAssessmentDialog.js";
import BotpressProgrammaticRunDialog from "../dialogs/botpressProgrammaticRunDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import BotpressAssessmentTable from "../ui/botpressAssessmentTable.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressAssessments = ({
  organizationODM,
  testBotODM,
  botpressBotODM,
  analyticsBotODM,
  userODM,
  testConfigurations,
  assessment,
  conversation,
  searchAssessmentRequest,
  deleteChatRequest,
  assessmentPagination,
  assessmentPageLoading,
  loadConversationRequest,
  conversationFeedback,
  deleteFeedbackRequest,
  updateFeedbackRequest,
  updateAssessmentRequest,
  deleteAssessmentsRequest,
  widgetSize = "full",
  minHeight = "333px",
  height = null,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [downloadConversation, setDownloadConversation] = useState(null);
  const [compareEntry, setCompareEntry] = useState(null);

  useEffect(() => {
    if (assessment != null) {
      setRows(assessment);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessment]);

  const onRequestNewPage = async pagination => {
    try {
      searchAssessmentRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  //#region load conversation number first
  const [retryCount, setRetryCount] = useState(-1);
  const [conversationNumber, setConversationNumber] = useState(null);
  const [assessmentConversationId, setAssessmentConversationId] = useState(null);

  useEffect(() => {
    if (assessmentConversationId != null) {
      //try to load
      try {
        const delayTimeFunction = setTimeout(() => {
          // setCountdown(prev => prev - 1)//lookup
          //look up the record, if that fails set the timer.
          let newPage = createOmniPaginationObject(null, 20);
          newPage.pageIndex = 0;
          newPage.sort = { createdAt: "desc" };
          newPage.query = {
            conversationId: {
              $regex: `${assessmentConversationId}`,
            },
          };
          newPage["botODM"] = testBotODM;
          BotpressAPI.searchConversations(newPage).then(theReturn => {
            if (theReturn.rows.length > 0) {
              //found it
              setConversationNumber(theReturn.rows[0].id);
            } else {
              setRetryCount(3);
            }
          });
        }, 250); //second delay, totally emperical
        return () => clearTimeout(delayTimeFunction);
      } catch (error) {
        console.log(error);
      }
    }

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentConversationId]);

  useEffect(() => {
    if (retryCount > 0 && assessmentConversationId != null) {
      //try to load
      try {
        const delayTimeFunction = setTimeout(() => {
          // setCountdown(prev => prev - 1)//lookup
          //look up the record, if that fails set the timer.
          let newPage = createOmniPaginationObject(null, 20);
          newPage.pageIndex = 0;
          newPage.sort = { createdAt: "desc" };
          newPage.query = {
            conversationId: {
              $regex: `${assessmentConversationId}`,
            },
          };
          newPage["botODM"] = testBotODM;
          BotpressAPI.searchConversations(newPage).then(theReturn => {
            if (theReturn.rows.length > 0) {
              //found it
              setConversationNumber(theReturn.rows[0].id);
            } else {
              setRetryCount(prev => prev - 1);
            }
          });
        }, 2000); // 3 second delay, totally emperical
        return () => clearTimeout(delayTimeFunction);
      } catch (error) {
        console.log(error);
      }
    } else if (retryCount === 0) {
      // Couldn't resolve a ConversationsTable number — e.g. conversations created
      // by a programmatic (Rerun / flagged batch) run aren't logged there.
      // Stop polling, but keep the loaded transcript so View Conversation still
      // opens; the dialog falls back to a generic title when no number is found.
      setAssessmentConversationId(null);
    }

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryCount, assessmentConversationId]);

  //#end Region

  useEffect(() => {
    if (selectedAssessment != null) {
      // These conversations live in the TEST bot, so scope the load to it
      // rather than defaulting to the PROD (botpressBotODM) bot.
      loadConversationRequest({
        conversationId: selectedAssessment.conversationId,
        botODM: testBotODM,
      });
      setAssessmentConversationId(selectedAssessment.conversationId);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssessment]);

  const [singleAssessmentEntry, setSingleAssessmentEntry] = useState(null);
  const [singleConversation, setSingleConversation] = useState(null);

  useEffect(() => {
    if (singleAssessmentEntry != null) {
      try {
        const credentialedPayload = {
          botODM: testBotODM,
          conversationId: singleAssessmentEntry.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setSingleConversation(returnedConversation);
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setSingleConversation(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleAssessmentEntry]);

  const [comparisonAssessments, setComparisonAssessments] = useState(null);
  const [comparisonConversationNewest, setComparisonConversationNewest] = useState(null);
  const [comparisonConversationOldest, setComparisonConversationOldest] = useState(null);

  useEffect(() => {
    if (comparisonAssessments != null && comparisonAssessments.length === 2) {
      if (
        comparisonAssessments[0].original.assessmentType !==
        comparisonAssessments[1].original.assessmentType
      ) {
        enqueueSnackbar("You man only compare Assessments of the same type", {
          variant: "error",
        });
        setComparisonConversationNewest(null);
        setComparisonConversationOldest(null);
        return;
      }
      let conversationNewestIndex = null;
      let conversationOldestIndex = null;

      //these are records still
      if (
        new Date(comparisonAssessments[0].createdAt) < new Date(comparisonAssessments[1].createdAt)
      ) {
        conversationOldestIndex = 0;
        conversationNewestIndex = 1;
      } else {
        conversationOldestIndex = 1;
        conversationNewestIndex = 0;
      }

      try {
        const credentialedPayload = {
          botODM: testBotODM,
          conversationId: comparisonAssessments[conversationOldestIndex].original.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setComparisonConversationOldest(returnedConversation);
          const credentialedPayload2 = {
            botODM: testBotODM,
            conversationId: comparisonAssessments[conversationNewestIndex].original.conversationId,
          };
          BotpressAPI.loadConversation(credentialedPayload2).then(returnedConversation2 => {
            setComparisonConversationNewest(returnedConversation2);
          });
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setComparisonConversationNewest(null);
      setComparisonConversationOldest(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisonAssessments]);

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${testBotODM.omniData.workspaceId}/bots/${testBotODM.omniData.botId}/conversations/${conversationId}/logs`;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };
  // Programmatic ("Quick") rerun: replay the conversation's questions against
  // the bot via the Chat API in a progress dialog, without opening the webchat.
  const [programmaticRerunEntry, setProgrammaticRerunEntry] = useState(null);

  const onProgrammaticRerun = assessmentRow => {
    if (assessmentRow == null) {
      return;
    }
    setProgrammaticRerunEntry(assessmentRow);
  };

  // After a Rerun finishes, re-point the assessment at the newly-created
  // conversation and refresh its run date. createdAt is a Botpress-managed system
  // column that can't be reset via an in-place upsert, so we insert a fresh row
  // (new "Date") and remove the old one — keeping a single, up-to-date checklist
  // entry rather than piling up duplicates.
  const onRerunComplete = async ({ conversationId, elapsedSeconds, questionCount }) => {
    const previous = programmaticRerunEntry;
    if (previous == null || conversationId == null) {
      return;
    }

    try {
      const userName = userODM
        ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}`
        : previous.userName || "Unknown User";

      const newRecord = {
        id: null,
        comment: previous.comment,
        questionCount: questionCount != null ? questionCount : previous.questionCount,
        archived: false,
        userName,
        conversationId,
        elapsedSeconds,
        assessmentType: previous.assessmentType,
        assessmentName: previous.assessmentName,
        configuration: previous.configuration,
        // Re-stamp with the current TEST bot version and reset status to New (0)
        // so the fresh run gets re-rated, falling back to the previous version
        // if the config table has no version row.
        version: getConfigurationVersion(testConfigurations) || previous.version || "",
        status: 0,
      };

      const addResult = await BotpressAPI.addAssessment({
        botODM: testBotODM,
        assessmentRecord: newRecord,
      });
      if (addResult && Object.hasOwn(addResult, "code")) {
        throw new Error(addResult.message || "Failed to save assessment");
      }

      // Only remove the stale entry once the replacement is safely written.
      if (previous.id != null) {
        await BotpressAPI.deleteAssessments({
          botODM: testBotODM,
          assessmentIDs: [previous.id],
        });
      }

      enqueueSnackbar("Assessment updated with the latest run.", { variant: "success" });
    } catch (error) {
      console.error("Failed to update assessment after rerun:", error);
      enqueueSnackbar(`Rerun completed, but the assessment was not updated: ${error.message}`, {
        variant: "error",
      });
    }
  };

  const [deleteRows, setDeleteRows] = useState(null);

  const onDeleteAssessments = rowsIDs => {
    try {
      if (rowsIDs == null) {
        return;
      }
      setDeleteRows(rowsIDs);
    } catch (error) {
      console.error("Error deleting table rows", error);
      setRows([]);
    }
  };

  const onRefreshTable = () => {
    let newPagination = deepCopyObject(assessmentPagination);
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };

  // Feedback shown in the View Conversation dialog belongs to TEST-bot
  // conversations, so scope add/edit/delete to the TEST bot rather than letting
  // the saga default to the PROD (botpressBotODM) bot. skipFeedbackReload keeps
  // it from reloading the co-located PROD feedback table with TEST data.
  const onUpdateTestFeedback = feedbackPayload =>
    updateFeedbackRequest({ ...feedbackPayload, botODM: testBotODM, skipFeedbackReload: true });
  const onDeleteTestFeedback = feedbackPayload =>
    deleteFeedbackRequest({ ...feedbackPayload, botODM: testBotODM, skipFeedbackReload: true });

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight} height={height}>
      <WidgetTitle
        title={"Assessments"}
        text={rows != null ? `${rows.length} Test Conversations` : "Test Conversations"}
      />

      {rows == null && <WidgetSkeleton />}
      {rows != null && (
        <WidgetSurveyContent>
          <BotpressAssessmentTable
            data={rows}
            isLoading={assessmentPageLoading}
            onEditEntry={setSelectedEntry}
            onRequestNewPage={onRequestNewPage}
            dataReduxPagination={assessmentPagination}
            onViewConversation={setSelectedAssessment}
            onAssessEntry={analyticsBotODM ? setSingleAssessmentEntry : null}
            onCompareAssessments={analyticsBotODM ? setComparisonAssessments : null}
            onCompareConversations={analyticsBotODM ? setCompareEntry : null}
            onDownloadEntry={setDownloadConversation}
            onHandleDeveloperLogs={onHandleDeveloperLogs}
            onProgrammaticRerun={onProgrammaticRerun}
            onDeleteAssessments={onDeleteAssessments}
            onRefreshTable={onRefreshTable}
            refreshTooltipTitle="Refresh assessments"
            supportedAssessmentTypes={organizationODM.omniData.supportedAssessmentTypes}
          />
        </WidgetSurveyContent>
      )}

      {selectedEntry != null && (
        <BotpressAssessmentDialog
          open={selectedEntry != null}
          assessmentRecord={selectedEntry}
          onAccept={theRecord => {
            updateAssessmentRequest({ assessmentRecord: theRecord });
            setSelectedEntry(null);
          }}
          onDelete={theRecord => {
            deleteAssessmentsRequest({ assessmentIDs: [theRecord.id] });
            setSelectedEntry(null);
          }}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedAssessment != null && conversation != null && (
        <BotpressConversationDialog
          conversationId={conversation.conversationId}
          conversation={conversation}
          conversationNumber={conversationNumber}
          botODM={testBotODM}
          dialogTitle={conversationNumber ? `Conversation #${conversationNumber}` : "Conversation"}
          //dialogTitle={`${selectedAssessment.assessmentType.includes("#") ? "Rerun of " + selectedAssessment.assessmentType : selectedAssessment.assessmentName}`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={onDeleteTestFeedback}
          updateFeedbackRequest={onUpdateTestFeedback}
          userODM={userODM}
          open={selectedAssessment != null}
          defaultValue={selectedAssessment}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedAssessment(null);
            setAssessmentConversationId(null);
            setConversationNumber(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedAssessment(null);

            setAssessmentConversationId(null);
            setConversationNumber(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedAssessment.conversationId });
            loadConversationRequest(null);
            setSelectedAssessment(null);
            setAssessmentConversationId(null);
            setConversationNumber(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}

      {singleConversation != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={singleConversation != null}
          singleConversation={singleConversation}
          onAccept={() => {
            setSingleAssessmentEntry(null);
          }}
          onClose={() => {
            setSingleAssessmentEntry(null);
          }}
        />
      )}

      {comparisonConversationNewest != null && comparisonConversationOldest != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={comparisonConversationNewest != null && comparisonConversationOldest != null}
          newestConversation={comparisonConversationNewest}
          oldestConversation={comparisonConversationOldest}
          onAccept={() => {
            setComparisonConversationNewest(null);
            setComparisonConversationOldest(null);
          }}
          onClose={() => {
            setComparisonConversationNewest(null);
            setComparisonConversationOldest(null);
          }}
        />
      )}

      {downloadConversation != null && (
        //todo replace this with a new dialog that supports a download format property
        <TextInputDialog
          inputLabel={`File Name`}
          open={downloadConversation}
          dialogTitle={"Download Conversation"}
          dialogMessage={`Please enter a file name for the conversation download`}
          defaultValue={`Conversation_${format(new Date(), "yyMMdd_hhmm")}`}
          inputPlaceholder={"File Name Here"}
          onAccept={theValue => {
            try {
              BotpressAPI.loadConversation({
                botODM: testBotODM,
                conversationId: downloadConversation.conversationId,
              }).then(returnedMessages => {
                handleDownloadConversation(returnedMessages.reverse(), theValue.split(".")[0]);
              });

              setDownloadConversation(null);
            } catch (error) {
              console.log(error);
            }
            setDownloadConversation(null);
          }}
          onClose={() => {
            setDownloadConversation(null);
          }}
        />
      )}

      {programmaticRerunEntry != null && (
        <BotpressProgrammaticRunDialog
          open={programmaticRerunEntry != null}
          botODM={testBotODM}
          conversationId={programmaticRerunEntry.conversationId}
          onRunComplete={onRerunComplete}
          dialogTitle={"Rerun"}
          onClose={() => {
            setProgrammaticRerunEntry(null);
            onRefreshTable();
          }}
        />
      )}

      {compareEntry != null && (
        <BotpressCompareConversationsDialog
          open={compareEntry != null}
          assessmentRow={compareEntry}
          testBotODM={testBotODM}
          botpressBotODM={botpressBotODM}
          analyticsBotODM={analyticsBotODM}
          onComplete={results => {
            updateAssessmentRequest({
              assessmentRecord: { ...compareEntry, analysisResults: results },
            });
            setCompareEntry(null);
            enqueueSnackbar("Comparison results saved to the assessment.", {
              variant: "success",
            });
          }}
          onClose={() => setCompareEntry(null)}
        />
      )}

      {deleteRows && (
        <AlertDialog
          open={deleteRows}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to delete these Assessments?"}
          onAccept={() => {
            deleteAssessmentsRequest({
              assessmentIDs: deleteRows,
            });
            setDeleteRows(null);
          }}
          onClose={() => {
            setDeleteRows(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  assessment: state.botpressSession.assessment,
  assessmentsStartDate: state.botpressSession.assessmentsStartDate,
  assessmentsEndDate: state.botpressSession.assessmentsEndDate,
  assessmentPagination: state.botpressSession.assessmentPagination,
  assessmentPageLoading: state.botpressSession.assessmentPageLoading,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  userODM: state.userSession.userODM,

  testBotODM: state.botpressSession.testBotODM,
  botpressBotODM: state.botpressSession.botpressBotODM,
  analyticsBotODM: state.botpressSession.analyticsBotODM,
  testConfigurations: state.botpressSession.testConfigurations,

  organizationODM: state.userSession.organizationODM,
});

const mapDispatchToProps = {
  searchAssessmentRequest: BotpressSessionActions.searchAssessmentRequest,
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  updateAssessmentRequest: BotpressSessionActions.updateAssessmentRequest,
  deleteAssessmentsRequest: BotpressSessionActions.deleteAssessmentsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressAssessments);
